import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { Calendar, Clock, MapPin, Users, DollarSign, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { LoadingInline, LoadingPage } from '@/components/ui/spinner'
import { formatTime, formatPhone, formatCurrency } from '@/lib/utils'
import { useEvento } from '@/hooks/use-eventos'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAuth } from '@/hooks/use-auth'
import { useCriarParticipante, useAtualizarParticipante, useUploadComprovante } from '@/hooks/use-participantes'
import { logger } from '@/lib/logger'
import { supabase } from '@/lib/supabase'
import { InscricaoSucessoCard } from '@/components/eventos/InscricaoSucessoCard'

export function EventoInscricaoPage() {
  const { eventoSlug } = useParams<{ eventoSlug?: string }>()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [inscricaoCompleta, setInscricaoCompleta] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  // Buscar evento real pelo slug
  const { data: eventoData, isLoading: isLoadingEvento, error: eventoError } = useEvento(eventoSlug)

  // Normalizar dados para uso na UI
  const evento = useMemo(() => eventoData ?? null, [eventoData])
  const isPaidEvent = useMemo(() => {
    if (!evento) return false
    const valor = evento.valor_inscricao ?? evento.valor ?? 0
    return !evento.gratuito && valor > 0
  }, [evento])

  const { user, perfil } = useAuth()

  const schema = useMemo(
    () =>
      z
        .object({
          nome: z.string().min(3, 'Informe seu nome completo'),
          // Campos de contato opcionais individualmente, mas pelo menos um é obrigatório
          email: z
            .string()
            .trim()
            .optional()
            .refine((v) => !v || z.string().email().safeParse(v).success, 'E-mail inválido'),
          telefone: z
            .string()
            .trim()
            .optional()
            .refine(
              (v) => !v || v.replace(/\D/g, '').length >= 10,
              'Celular inválido'
            ),
          valorPago: z
            .string()
            .optional()
            .refine(
              (v) => {
                if (!v || v.trim() === '') return true
                const valor = parseFloat(v.replace(/[^\d,]/g, '').replace(',', '.'))
                return !isNaN(valor) && valor >= 0
              },
              'Valor inválido'
            ),
          observacoes: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          const hasEmail = !!data.email && data.email.trim().length > 0
          const phoneDigits = (data.telefone ?? '').replace(/\D/g, '')
          const hasPhone = phoneDigits.length >= 10
          if (!hasEmail && !hasPhone) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['email'],
              message: 'Informe e-mail ou celular',
            })
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['telefone'],
              message: 'Informe e-mail ou celular',
            })
          }
        }),
    []
  )

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      nome: perfil?.nome || '',
      email: user?.email || '',
      telefone: perfil?.telefone || '',
      valorPago: '',
      observacoes: '',
    },
  })

  const criarParticipante = useCriarParticipante()
  const atualizarParticipante = useAtualizarParticipante()
  const uploadComprovante = useUploadComprovante()


  if (isLoadingEvento) {
    return (
      <div className="min-h-screen p-6">
        <LoadingPage message="Carregando dados do evento..." />
      </div>
    )
  }

  if (!evento || eventoError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Evento não encontrado</CardTitle>
            <CardDescription>O evento que você está procurando não existe.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate(-1)} className="w-full">
              Ver Eventos Disponíveis
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const onSubmit = async (data: z.infer<typeof schema>) => {
    if (!evento) return
    setIsSubmitting(true)
    try {
      if (isPaidEvent && !selectedFile) {
        logger.warn('Comprovante de pagamento é obrigatório para eventos pagos.', { context: 'INSCRICAO_EVENTO' })
        setIsSubmitting(false)
        return
      }

      const novoParticipante = await criarParticipante.mutateAsync({
        evento_id: evento.id,
        nome: data.nome,
        email: data.email,
        telefone: data.telefone,
        observacoes: data.observacoes,
        usuario_id: perfil?.id || null,
        status: isPaidEvent ? 'pendente' : 'confirmado',
      })

      if (isPaidEvent && selectedFile) {
        const publicUrl = await uploadComprovante.mutateAsync({
          file: selectedFile,
          igrejaId: evento.igreja_id,
          eventoId: evento.id,
          participanteId: novoParticipante.id,
        })

        await atualizarParticipante.mutateAsync({
          id: novoParticipante.id,
          dados: { comprovante_url: publicUrl },
        })
      }

      // Atualizar valor da entrada se fornecido
      if (isPaidEvent && data.valorPago) {
        const valorNumerico = parseFloat(data.valorPago.replace(/[^\d,]/g, '').replace(',', '.'))
        if (!isNaN(valorNumerico) && valorNumerico > 0) {
          if (!supabase) throw new Error('Supabase client not initialized')
          await supabase.from('participante_pagamentos').insert({
            participante_id: novoParticipante.id,
            valor: valorNumerico,
            status: 'pendente', // O status do pagamento fica pendente até confirmação
            metodo: 'pix', // Assumindo pix, pode ser ajustado
          })
        }
      }

      logger.info('Inscrição realizada com sucesso', {
        context: 'INSCRICAO_EVENTO',
        eventoId: evento.id,
        participanteId: novoParticipante.id,
      })

      setInscricaoCompleta(true)
    } catch (err: any) {
      logger.error('Erro ao realizar inscrição', { context: 'INSCRICAO_EVENTO', error: err?.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (inscricaoCompleta) {
    return (
      <InscricaoSucessoCard
        tituloEvento={evento.titulo}
        statusInscricao={isPaidEvent ? 'pendente' : 'confirmado'}
        isPaidEvent={isPaidEvent}
      />
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/10">
      <div className="mx-auto max-w-4xl p-4 py-8 sm:p-6 lg:p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 space-y-1"
        >
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Inscrição no Evento</h1>
          <p className="text-sm text-muted-foreground">
            Preencha seus dados para confirmar sua participação
          </p>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Event Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-1"
          >
            <Card className="sticky top-4">
              {evento.capa_url && (
                <div className="relative aspect-video overflow-hidden rounded-t-lg">
                  <img src={evento.capa_url} alt={evento.titulo} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <Badge className="absolute right-3 top-3 bg-white/90 text-foreground backdrop-blur-sm">
                    {evento.status}
                  </Badge>
                </div>
              )}
              <CardHeader>
                <CardTitle className="line-clamp-2">{evento.titulo}</CardTitle>
                {evento.descricao && (
                  <CardDescription className="line-clamp-3">{evento.descricao}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span>
                    {new Date(evento.inicio_em).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{formatTime(evento.inicio_em)}</span>
                </div>
                {evento.local && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{evento.local}</span>
                  </div>
                )}
                {evento.capacidade && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{evento.capacidade} vagas disponíveis</span>
                  </div>
                )}
                {evento.gratuito || !evento.valor_inscricao ? (
                  <div className="flex items-center gap-2 rounded-lg bg-primary/10 p-3 text-sm font-medium text-primary">
                    <DollarSign className="h-4 w-4" />
                    <span>Evento Gratuito</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-lg bg-primary/10 p-3 text-sm font-medium text-primary">
                    <DollarSign className="h-4 w-4" />
                    <span>Valor da inscrição: {formatCurrency(evento.valor_inscricao ?? evento.valor ?? 0)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <Card>
              <CardHeader>
                <CardTitle>Dados para Inscrição</CardTitle>
                <CardDescription>
                  Preencha as informações abaixo para confirmar sua participação
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">
                      Nome Completo <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="nome"
                      placeholder="Digite seu nome completo"
                      {...register('nome')}
                      required
                    />
                    {errors.nome && (
                      <p className="text-sm text-destructive">{errors.nome.message}</p>
                    )}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        {...register('email')}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="telefone">Celular</Label>
                      <Input
                        id="telefone"
                        type="tel"
                        placeholder="(00) 00000-0000"
                        {...register('telefone', {
                          onChange: (e) => setValue('telefone', formatPhone(e.target.value)),
                        })}
                      />
                      {errors.telefone && (
                        <p className="text-sm text-destructive">{errors.telefone.message}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Informe pelo menos um contato: e-mail ou celular.</p>

                  <div className="space-y-2">
                    <Label htmlFor="observacoes">Observações</Label>
                    <Textarea
                      id="observacoes"
                      placeholder="Alguma informação adicional? (opcional)"
                      rows={3}
                      {...register('observacoes')}
                    />
                  </div>

                  {isPaidEvent ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="valorPago">
                          Valor Pago na Inscrição <span className="text-muted-foreground">(opcional)</span>
                        </Label>
                        <Input
                          id="valorPago"
                          type="text"
                          placeholder={`Sugerido: ${formatCurrency(evento.valor_inscricao ?? evento.valor ?? 0)}`}
                          {...register('valorPago')}
                        />
                        {errors.valorPago && (
                          <p className="text-sm text-destructive">{errors.valorPago.message}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Valor sugerido: {formatCurrency(evento.valor_inscricao ?? evento.valor ?? 0)}.
                          O valor final será confirmado pelo comprovante de pagamento.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="comprovante">
                          Comprovante de Pagamento <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="comprovante"
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null
                            setSelectedFile(file)
                          }}
                        />
                        {evento.chave_pix && (
                          <div className="rounded-md bg-muted/50 p-3 text-sm">
                            <p className="font-medium">Pagamento via Pix</p>
                            <p className="text-muted-foreground">Chave Pix: {evento.chave_pix}</p>
                            <p className="text-muted-foreground">
                              Envie o comprovante para confirmar a inscrição.
                            </p>
                          </div>
                        )}
                        <p className="text-xs text-amber-600 dark:text-amber-500">
                          ⚠️ O valor será confirmado pela equipe através do comprovante enviado.
                        </p>
                      </div>
                    </>
                  ) : null}

                  <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
                    <p>
                      Ao se inscrever, você concorda em compartilhar suas informações com os
                      organizadores do evento.
                    </p>
                  </div>

                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={isSubmitting} className="gap-2">
                      {isSubmitting ? (
                        <LoadingInline message="Processando..." size="sm" />
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4" />
                          Confirmar Inscrição
                        </>
                      )}
                    </Button>
                  </div>
                  {!user && (
                    <p className="text-xs text-muted-foreground">
                      Já possui conta?{' '}
                      <Button variant="link" className="px-0" onClick={() => navigate('/login')}>
                        Faça login
                      </Button>{' '}
                      para preencher automaticamente seus dados.
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
